import React, { FunctionComponent } from "react";
import { Button, Form } from "@plex/react-components";
import { IViewController, useViewController } from "../ViewContext";
import { UserDefinedSchemasGrid } from "./UserDefinedSchemasGrid";
import { openConfirmDialog } from "../Util/DialogUtil";
import { deleteSchema, getSchema } from "../NodeTypes/DataSchemas";
import { getSchemaObjectName, IDocumentSchema } from "../FlowDocument/FlowDocumentModel";

export const UserDefinedSchemasForm: FunctionComponent = () => {
  const {
    setUserDefinedSchemasEditorState,
    userDefinedSchemasEditorState,
    setUserDefinedSchemaDetailsDialogState,
    userDefinedSchemaDetailsDialogState,
    userDefinedSchemasAddDialogState,
    setUserDefinedSchemasAddDialogState
  }: IViewController = useViewController();

  return (
    <Form className="user-defined-schemas-editor-form">
      <Form.Body>
        <Form.Section title="Details">
          <Form.Row className="user-defined-schemas-editor-form-input-row">
            <UserDefinedSchemasGrid
              onSelectionChanged={(row) => {
                if (row?.id !== userDefinedSchemasEditorState.selectedSchemaId) {
                  userDefinedSchemasEditorState.selectedSchemaId = row?.id;
                  userDefinedSchemasEditorState.selectedSchemaName = row?.name;
                  userDefinedSchemasEditorState.selectedSchemaInUse = row?.inUse ?? false;
                  setUserDefinedSchemasEditorState({ ...userDefinedSchemasEditorState });
                }
              }}
            />
          </Form.Row>
        </Form.Section>
      </Form.Body>
      <Form.Footer>
        <Form.Row className="user-defined-schemas-editor-form-button-row">
          <Button
            variance={"link"}
            data-testid="user-defined-schemas-editor-cancel"
            as={"button"}
            type="button"
            onClick={() => {
              userDefinedSchemasEditorState.show = false;
              userDefinedSchemasEditorState.selectedSchemaId = undefined;
              userDefinedSchemasEditorState.selectedSchemaName = undefined;
              setUserDefinedSchemasEditorState({ ...userDefinedSchemasEditorState });
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid="user-defined-schemas-editor-add"
            variance={"primary"}
            as={"button"}
            type="button"
            onClick={() => {
              setUserDefinedSchemasAddDialogState({ ...userDefinedSchemasAddDialogState, show: true });
            }}
          >
            Add
          </Button>
          <Button
            data-testid="user-defined-schemas-editor-delete"
            variance={"primary"}
            as={"button"}
            type="button"
            title={
              userDefinedSchemasEditorState.selectedSchemaInUse
                ? "Cannot delete schema that is being used"
                : !userDefinedSchemasEditorState.selectedSchemaId
                ? "Select a schema"
                : ""
            }
            disabled={
              !userDefinedSchemasEditorState.selectedSchemaId || userDefinedSchemasEditorState.selectedSchemaInUse
            }
            onClick={() =>
              openConfirmDialog("Are you sure you want to delete this user defined object?", () => {
                deleteSchema(userDefinedSchemasEditorState.selectedSchemaId!);
                userDefinedSchemasEditorState.selectedSchemaId = undefined;
                userDefinedSchemasEditorState.selectedSchemaName = undefined;
                setUserDefinedSchemasEditorState({ ...userDefinedSchemasEditorState });
              })
            }
          >
            Delete
          </Button>
          <Button
            data-testid="user-defined-schemas-editor-edit"
            variance={"primary"}
            as={"button"}
            type="button"
            disabled={!userDefinedSchemasEditorState.selectedSchemaId}
            title={!userDefinedSchemasEditorState.selectedSchemaId ? "Select a schema" : ""}
            onClick={() => {
              const schema = getSchema(
                userDefinedSchemasEditorState.selectedSchemaId ?? ""
              ) as unknown as IDocumentSchema;
              const schemaName = getSchemaObjectName(schema);
              setUserDefinedSchemaDetailsDialogState({
                ...userDefinedSchemaDetailsDialogState,
                show: true,
                schemaName: schemaName,
                schemaInUse: userDefinedSchemasEditorState.selectedSchemaInUse,
                selectedPropertyName: undefined,
                loopVariable: false
              });
            }}
          >
            Edit
          </Button>
          <Button
            data-testid="user-defined-schemas-editor-ok"
            variance={"primary"}
            as={"button"}
            type="button"
            onClick={() => {
              userDefinedSchemasEditorState.show = false;
              userDefinedSchemasEditorState.selectedSchemaId = undefined;
              userDefinedSchemasEditorState.selectedSchemaName = undefined;
              setUserDefinedSchemasEditorState({ ...userDefinedSchemasEditorState });
            }}
          >
            OK
          </Button>
        </Form.Row>
      </Form.Footer>
    </Form>
  );
};
